import type {
  Bindings,
  BulkPublicationVersionDto,
  BulkPublicationVersionResponse,
  CreatePublicationDto,
  NamedId,
  Page,
  PatchPublicationDto,
  PreviewPublicationDto,
  Publication,
  PublicationListItem,
  PublicationResponse,
  PublicationsQuery,
  UUID,
} from "../../interface/publications"
import type { Occurrence } from "../../interface/occurrences"
import { isExternalId } from "../../utils/ids"
import {
  assertPatch,
  assertRevision,
  businessApi,
  postBotTags,
  requestQuery,
} from "./api"

const namedIdBody = (value: NamedId): NamedId => {
  if (!value || !isExternalId(value.id))
    throw new TypeError("Invalid external ID")
  return { id: value.id, name: value.name }
}

const bindingsBody = (bindings: Bindings): Bindings => {
  if (!bindings || typeof bindings !== "object" || Array.isArray(bindings))
    throw new TypeError("Bindings must be an object keyed by slot UUID")
  return Object.fromEntries(
    Object.entries(bindings).map(([slotId, value]) => {
      if (
        !value ||
        typeof value !== "object" ||
        Array.isArray(value) ||
        "botId" in value
      )
        throw new TypeError("Binding writes require URL or named bot metadata")
      if ("url" in value) {
        if ("bot" in value || "botType" in value || "referral" in value)
          throw new TypeError("URL and bot bindings cannot be mixed")
        return [slotId, { url: value.url }]
      }
      return [
        slotId,
        {
          bot: namedIdBody(value.bot),
          ...(value.botType !== undefined
            ? { botType: namedIdBody(value.botType) }
            : {}),
          ...(value.referral !== undefined
            ? { referral: namedIdBody(value.referral) }
            : {}),
        },
      ]
    }),
  )
}

const transformPublicationResponse = (
  response: PublicationResponse,
): Publication => response.publication

export const publicationsApi = businessApi.injectEndpoints({
  endpoints: (build) => ({
    createPublication: build.mutation<Publication, CreatePublicationDto>({
      queryFn: requestQuery(
        (body) => ({
          url: "publications",
          method: "POST",
          body: {
            name: body.name,
            templateVersionId: body.templateVersionId,
            bindings: bindingsBody(body.bindings),
          },
        }),
        transformPublicationResponse,
      ),
      invalidatesTags: (_result, error) =>
        error ? [] : [postBotTags.publications],
    }),
    getPublications: build.query<
      Page<PublicationListItem>,
      PublicationsQuery | void
    >({
      query: (args) => ({
        url: "publications",
        params: {
          page: args?.page,
          limit: args?.limit,
          templateId: args?.templateId,
          templateVersionId: args?.templateVersionId,
        },
      }),
      providesTags: (result) => [
        postBotTags.publications,
        ...(result?.data.map(({ id }) => postBotTags.publication(id)) ?? []),
      ],
    }),
    getPublication: build.query<Publication, UUID>({
      query: (id) => `publications/${id}`,
      transformResponse: transformPublicationResponse,
      providesTags: (_result, _error, id) => [postBotTags.publication(id)],
    }),
    patchPublication: build.mutation<
      Publication,
      { id: UUID; body: PatchPublicationDto }
    >({
      queryFn: requestQuery(({ id, body }) => {
        assertPatch(body.revision, [
          body.name,
          body.templateVersionId,
          body.bindings,
        ])
        return {
          url: `publications/${id}`,
          method: "PATCH",
          body: {
            revision: body.revision,
            name: body.name,
            templateVersionId: body.templateVersionId,
            bindings:
              body.bindings === undefined
                ? undefined
                : bindingsBody(body.bindings),
          },
        }
      }, transformPublicationResponse),
      invalidatesTags: (_result, error, { id }) =>
        error ? [] : [postBotTags.publications, postBotTags.publication(id)],
    }),
    bulkPublicationVersion: build.mutation<
      BulkPublicationVersionResponse,
      BulkPublicationVersionDto
    >({
      queryFn: requestQuery((body) => ({
        url: "publications/bulk-version",
        method: "POST",
        body: {
          items: body.items.map((item) => {
            assertRevision(item.revision)
            return {
              publicationId: item.publicationId,
              revision: item.revision,
              templateVersionId: item.templateVersionId,
              bindings:
                item.bindings === undefined
                  ? undefined
                  : bindingsBody(item.bindings),
            }
          }),
        },
      })),
      // Failed items may be stale; refresh all attempted resources, not just successes.
      invalidatesTags: (result) =>
        result
          ? [
              postBotTags.publications,
              ...result.results.map(({ publicationId }) =>
                postBotTags.publication(publicationId),
              ),
            ]
          : [],
    }),
    previewPublication: build.mutation<
      Occurrence,
      { id: UUID } & PreviewPublicationDto
    >({
      query: ({ id, pin }) => ({
        url: `publications/${id}/preview`,
        method: "POST",
        body: pin === true ? { pin: true } : {},
      }),
    }),
  }),
})

export const {
  useCreatePublicationMutation,
  useGetPublicationsQuery,
  useGetPublicationQuery,
  useLazyGetPublicationQuery,
  usePatchPublicationMutation,
  useBulkPublicationVersionMutation,
  usePreviewPublicationMutation,
} = publicationsApi
